import { ENGINE_WORKLET_SOURCE } from './engine-worklet-source.js';
import { AdaptiveScore, type ScoreMode } from './music.js';

export type AudioDynamics = 'wide' | 'standard' | 'night';

export interface AudioMix {
  master: number;
  music: number;
  effects: number;
  ui: number;
  dynamics: AudioDynamics;
}

/** Group outputs a sound can be routed into. Gameplay groups pass through the
 * pause muffle; music and interface sounds stay clear while a menu is open. */
export interface AudioBuses {
  engine: GainNode;
  effects: GainNode;
  ambience: GainNode;
  music: GainNode;
  ui: GainNode;
  /** Shared plate/hall reverb send; anything may feed it. */
  reverb: GainNode;
}

const MUTED_KEY = 'nightshift:muted';
const MASTER_HEADROOM = 0.86;

const DYNAMICS: Record<AudioDynamics, { threshold: number; ratio: number; knee: number; makeup: number }> = {
  // Wide keeps transients for good headphones and home cinema setups.
  wide: { threshold: -12, ratio: 1.6, knee: 10, makeup: 1.0 },
  standard: { threshold: -20, ratio: 2.6, knee: 9, makeup: 1.18 },
  // Night mode squeezes crashes and lifts quiet detail for laptop speakers or
  // late sessions with neighbours.
  night: { threshold: -32, ratio: 6, knee: 6, makeup: 1.6 },
};

/**
 * One AudioContext for the whole app. Menus, the race and the score all route
 * through the same buses and mastering chain:
 *
 *   engine/effects/ambience → pause muffle ┐
 *   music → duck ───────────────────────────┼→ glue compressor → soft clip → limiter → master → out
 *   ui ─────────────────────────────────────┘        ↑
 *   reverb send → convolver (generated hall) ─────────┘
 *
 * The context is created lazily on the first real gesture so browsers never
 * block it, it suspends while the tab is hidden, and the shared mute
 * preference lives here so every surface agrees on it.
 */
export class AudioHub {
  private context: AudioContext | null = null;
  private buses: AudioBuses | null = null;
  private masterGain: GainNode | null = null;
  private glue: DynamicsCompressorNode | null = null;
  private makeup: GainNode | null = null;
  private muffle: BiquadFilterNode | null = null;
  private pauseGain: GainNode | null = null;
  private musicDuck: GainNode | null = null;
  private musicPauseGain: GainNode | null = null;
  private failed = false;
  private muted = readMutedPreference();
  private mix: AudioMix = { master: 1, music: 1, effects: 1, ui: 1, dynamics: 'standard' };
  private paused = false;
  private listenersInstalled = false;
  private suspendedForVisibility = false;
  private readonly muteListeners = new Set<(muted: boolean) => void>();
  private readonly readyListeners = new Set<(context: AudioContext) => void>();
  private readonly bufferCache = new Map<string, AudioBuffer>();
  private analyser: AnalyserNode | null = null;
  private engineWorkletState: 'idle' | 'loading' | 'ready' | 'failed' = 'idle';
  private engineWorkletPromise: Promise<boolean> | null = null;
  score: AdaptiveScore | null = null;
  private scoreMode: ScoreMode = 'off';

  /** Installs the gesture unlock and visibility handlers once. */
  install(): void {
    if (this.listenersInstalled || typeof window === 'undefined' || typeof document === 'undefined') return;
    this.listenersInstalled = true;
    // Capture phase: menu and touch-control handlers stop propagation, and the
    // unlock must still see the very first gesture.
    window.addEventListener('pointerdown', this.unlock, { capture: true, passive: true });
    window.addEventListener('keydown', this.unlock, { capture: true });
    window.addEventListener('touchend', this.unlock, { capture: true, passive: true });
    window.addEventListener('click', this.unlock, { capture: true });
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  get isMuted(): boolean {
    return this.muted;
  }

  get currentMix(): Readonly<AudioMix> {
    return this.mix;
  }

  /** The live context, or null before the first gesture / without Web Audio. */
  get audioContext(): AudioContext | null {
    return this.context;
  }

  get outputs(): AudioBuses | null {
    return this.buses;
  }

  get engineWorkletReady(): boolean {
    return this.engineWorkletState === 'ready';
  }

  /** Creates (or returns) the context and starts resuming it. Safe to call from
   * any gesture handler; never throws. */
  ensure(): AudioContext | null {
    if (this.failed) return null;
    if (!this.context) {
      try {
        this.createGraph();
      } catch {
        this.teardown();
        this.failed = true;
        return null;
      }
    }
    const context = this.context;
    if (context && context.state === 'suspended' && !this.suspendedForVisibility) {
      try {
        void context.resume().catch(() => {
          if (this.context === context && isClosed(context)) this.teardown();
        });
      } catch {
        if (this.context === context && isClosed(context)) this.teardown();
      }
    }
    return this.context;
  }

  readonly unlock = (): void => {
    this.ensure();
  };

  onReady(listener: (context: AudioContext) => void): () => void {
    this.readyListeners.add(listener);
    if (this.context && this.buses) listener(this.context);
    return () => this.readyListeners.delete(listener);
  }

  onMuteChange(listener: (muted: boolean) => void): () => void {
    this.muteListeners.add(listener);
    return () => this.muteListeners.delete(listener);
  }

  setMuted(muted: boolean): void {
    if (this.muted === muted) return;
    this.muted = muted;
    writeMutedPreference(muted);
    this.applyMaster(0.03);
    if (!muted) this.ensure();
    for (const listener of this.muteListeners) listener(muted);
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  setMix(mix: Partial<AudioMix>): void {
    this.mix = {
      master: clamp01(mix.master ?? this.mix.master),
      music: clamp01(mix.music ?? this.mix.music),
      effects: clamp01(mix.effects ?? this.mix.effects),
      ui: clamp01(mix.ui ?? this.mix.ui),
      dynamics: mix.dynamics && mix.dynamics in DYNAMICS ? mix.dynamics : this.mix.dynamics,
    };
    if (!this.context || !this.buses) return;
    const now = this.context.currentTime;
    this.buses.music.gain.setTargetAtTime(perceptual(this.mix.music), now, 0.05);
    this.buses.ui.gain.setTargetAtTime(perceptual(this.mix.ui) * 0.9, now, 0.03);
    this.gameplayGain = perceptual(this.mix.effects);
    for (const bus of [this.buses.engine, this.buses.effects, this.buses.ambience]) {
      bus.gain.setTargetAtTime(this.gameplayGain, now, 0.05);
    }
    this.applyDynamics();
    this.applyMaster(0.05);
  }

  private gameplayGain = 1;

  /** Settings/pause overlay: gameplay goes behind a closed door, the score dips. */
  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    if (!this.context || !this.muffle || !this.musicPauseGain) return;
    const now = this.context.currentTime;
    this.muffle.frequency.cancelScheduledValues(now);
    this.muffle.frequency.setTargetAtTime(paused ? 520 : 20_000, now, paused ? 0.07 : 0.16);
    this.musicPauseGain.gain.setTargetAtTime(paused ? 0.55 : 1, now, 0.2);
    this.pauseGain?.gain.setTargetAtTime(paused ? 0.32 : 1, now, paused ? 0.08 : 0.2);
  }

  /** Which part of the score should play; applied as soon as audio unlocks. */
  setScoreMode(mode: ScoreMode): void {
    this.scoreMode = mode;
    this.score?.setMode(mode);
  }

  /** Short sidechain-style dip of the score so crashes and cues cut through. */
  duckMusic(depth: number, holdSeconds: number): void {
    if (!this.context || !this.musicDuck) return;
    const now = this.context.currentTime;
    const target = 1 - clamp01(depth) * 0.75;
    const param = this.musicDuck.gain;
    param.cancelScheduledValues(now);
    param.setTargetAtTime(target, now, 0.015);
    param.setTargetAtTime(1, now + Math.max(0.05, holdSeconds), 0.35);
  }

  /** Lazily generated, cached buffers (noise beds, drum hits, loops). */
  buffer(key: string, build: (context: AudioContext) => AudioBuffer): AudioBuffer | null {
    const context = this.context;
    if (!context) return null;
    let buffer = this.bufferCache.get(key);
    if (!buffer) {
      buffer = build(context);
      this.bufferCache.set(key, buffer);
    }
    return buffer;
  }

  /** Loads the engine AudioWorklet once; resolves false where unsupported. */
  loadEngineWorklet(source: string = ENGINE_WORKLET_SOURCE): Promise<boolean> {
    if (this.engineWorkletPromise) return this.engineWorkletPromise;
    const context = this.context;
    if (!context?.audioWorklet || typeof Blob === 'undefined' || typeof URL?.createObjectURL !== 'function') {
      this.engineWorkletState = 'failed';
      return Promise.resolve(false);
    }
    this.engineWorkletState = 'loading';
    const url = URL.createObjectURL(new Blob([source], { type: 'application/javascript' }));
    this.engineWorkletPromise = context.audioWorklet.addModule(url).then(() => {
      this.engineWorkletState = 'ready';
      return true;
    }, () => {
      this.engineWorkletState = 'failed';
      return false;
    }).finally(() => URL.revokeObjectURL(url));
    return this.engineWorkletPromise;
  }

  /** Dev/diagnostic: peak and RMS (dBFS) of the final output. */
  meter(): { peakDb: number; rmsDb: number; state: string } | null {
    if (!this.analyser || !this.context) return null;
    const data = new Float32Array(this.analyser.fftSize);
    this.analyser.getFloatTimeDomainData(data);
    let peak = 0;
    let sum = 0;
    for (const value of data) {
      peak = Math.max(peak, Math.abs(value));
      sum += value * value;
    }
    const toDb = (value: number) => (value > 0 ? 20 * Math.log10(value) : -120);
    return { peakDb: toDb(peak), rmsDb: toDb(Math.sqrt(sum / data.length)), state: this.context.state };
  }

  private createGraph(): void {
    if (typeof window === 'undefined') throw new Error('No window');
    const AudioContextConstructor = window.AudioContext ?? (window as Window & {
      webkitAudioContext?: typeof AudioContext;
    }).webkitAudioContext;
    if (!AudioContextConstructor) throw new Error('Web Audio unavailable');
    const context = new AudioContextConstructor({ latencyHint: 'interactive' });
    this.context = context;

    const glue = context.createDynamicsCompressor();
    glue.attack.value = 0.018;
    glue.release.value = 0.24;
    this.glue = glue;
    const makeup = context.createGain();
    this.makeup = makeup;
    const clipper = context.createWaveShaper();
    clipper.curve = softClipCurve();
    clipper.oversample = '2x';
    const limiter = context.createDynamicsCompressor();
    limiter.threshold.value = -1.5;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.1;
    const masterGain = context.createGain();
    masterGain.gain.value = 0;
    this.masterGain = masterGain;
    glue.connect(makeup).connect(clipper).connect(limiter).connect(masterGain).connect(context.destination);
    const analyser = context.createAnalyser();
    analyser.fftSize = 2048;
    masterGain.connect(analyser);
    this.analyser = analyser;

    const muffle = context.createBiquadFilter();
    muffle.type = 'lowpass';
    muffle.frequency.value = this.paused ? 520 : 20_000;
    muffle.Q.value = 0.5;
    // A paused race freezes the engine at its last note; drop it well back so
    // the pause screen never drones.
    const pauseGain = context.createGain();
    pauseGain.gain.value = this.paused ? 0.32 : 1;
    muffle.connect(pauseGain).connect(glue);
    this.muffle = muffle;
    this.pauseGain = pauseGain;

    const engine = context.createGain();
    const effects = context.createGain();
    const ambience = context.createGain();
    for (const bus of [engine, effects, ambience]) bus.connect(muffle);

    const music = context.createGain();
    const musicDuck = context.createGain();
    const musicPauseGain = context.createGain();
    musicPauseGain.gain.value = this.paused ? 0.55 : 1;
    music.connect(musicDuck).connect(musicPauseGain).connect(glue);
    this.musicDuck = musicDuck;
    this.musicPauseGain = musicPauseGain;

    const ui = context.createGain();
    ui.connect(glue);

    const reverb = context.createGain();
    const convolver = context.createConvolver();
    convolver.buffer = createHallImpulse(context, 2.1);
    const reverbReturn = context.createGain();
    reverbReturn.gain.value = 0.9;
    // Reverb tails are pre-filtered so the low end stays tight.
    const reverbTone = context.createBiquadFilter();
    reverbTone.type = 'highpass';
    reverbTone.frequency.value = 180;
    reverb.connect(reverbTone).connect(convolver).connect(reverbReturn).connect(muffle);

    this.buses = { engine, effects, ambience, music, ui, reverb };
    this.setMix(this.mix);
    this.applyMaster(0.25);
    this.score = new AdaptiveScore(context, music, reverb);
    this.score.setMode(this.scoreMode);
    void this.loadEngineWorklet();
    for (const listener of this.readyListeners) listener(context);
  }

  private applyDynamics(): void {
    if (!this.glue || !this.makeup) return;
    const preset = DYNAMICS[this.mix.dynamics];
    this.glue.threshold.value = preset.threshold;
    this.glue.ratio.value = preset.ratio;
    this.glue.knee.value = preset.knee;
    this.makeup.gain.value = preset.makeup;
  }

  private applyMaster(timeConstant: number): void {
    if (!this.context || !this.masterGain) return;
    const target = this.muted ? 0 : perceptual(this.mix.master) * MASTER_HEADROOM;
    this.masterGain.gain.setTargetAtTime(target, this.context.currentTime, timeConstant);
  }

  private readonly onVisibilityChange = (): void => {
    const context = this.context;
    if (!context) return;
    try {
      if (document.hidden) {
        if (context.state === 'running') {
          this.suspendedForVisibility = true;
          void context.suspend().catch(() => undefined);
        }
      } else if (this.suspendedForVisibility) {
        this.suspendedForVisibility = false;
        void context.resume().catch(() => undefined);
      }
    } catch {
      // Visibility changes must never surface audio errors.
    }
  };

  private teardown(): void {
    const context = this.context;
    this.score?.dispose();
    this.score = null;
    this.context = null;
    this.buses = null;
    this.masterGain = null;
    this.glue = null;
    this.makeup = null;
    this.muffle = null;
    this.pauseGain = null;
    this.musicDuck = null;
    this.musicPauseGain = null;
    this.analyser = null;
    this.bufferCache.clear();
    this.engineWorkletState = 'idle';
    this.engineWorkletPromise = null;
    if (!context) return;
    try {
      void context.close().catch(() => undefined);
    } catch {
      // Older implementations can throw while closing a failed context.
    }
  }
}

/** Equal-loudness-ish fader law: 50% on a slider sounds roughly half as loud. */
export function perceptual(value: number): number {
  const clamped = clamp01(value);
  return clamped * clamped;
}

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
}

function isClosed(context: BaseAudioContext): boolean {
  return context.state === 'closed';
}

function softClipCurve(): Float32Array<ArrayBuffer> {
  const samples = 2_048;
  const curve = new Float32Array(samples);
  const drive = 1.25;
  const norm = Math.tanh(drive);
  for (let index = 0; index < samples; index += 1) {
    const x = (index / (samples - 1)) * 2 - 1;
    curve[index] = Math.tanh(x * drive) / norm;
  }
  return curve;
}

/** Decorrelated stereo hall: sparse early reflections into a dense, darkening
 * exponential tail. Generated once per context. */
export function createHallImpulse(context: BaseAudioContext, seconds: number): AudioBuffer {
  const rate = context.sampleRate;
  const length = Math.floor(rate * seconds);
  const buffer = context.createBuffer(2, length, rate);
  for (let channelIndex = 0; channelIndex < 2; channelIndex += 1) {
    const channel = buffer.getChannelData(channelIndex);
    let seed = 0x9e3779b9 ^ (channelIndex * 0x85ebca6b);
    const random = () => {
      seed ^= seed << 13;
      seed ^= seed >>> 17;
      seed ^= seed << 5;
      return ((seed >>> 0) / 0xffffffff) * 2 - 1;
    };
    // Early reflections.
    for (let tap = 0; tap < 14; tap += 1) {
      const at = Math.floor(rate * (0.008 + tap * 0.0067 + (random() + 1) * 0.004));
      if (at < length) channel[at] = (channel[at] ?? 0) + random() * 0.55 * (1 - tap / 16);
    }
    let low = 0;
    for (let index = 0; index < length; index += 1) {
      const t = index / rate;
      const decay = Math.exp(-t * (6.9 / seconds));
      // The tail darkens as it decays, like air absorption.
      const brightness = 0.15 + 0.8 * Math.exp(-t * 2.2);
      const white = random();
      low += (white - low) * brightness;
      const fadeIn = Math.min(1, t / 0.02);
      channel[index] = (channel[index] ?? 0) + low * decay * fadeIn * 0.6;
    }
  }
  return buffer;
}

function readMutedPreference(): boolean {
  try {
    return localStorage.getItem(MUTED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMutedPreference(muted: boolean): void {
  try {
    localStorage.setItem(MUTED_KEY, muted ? '1' : '0');
  } catch {
    // Sound remains usable when storage is unavailable or blocked.
  }
}

export const audioHub = new AudioHub();
